import { Typography } from '@kizen/kds/Typography';
import ObjectSelector from '__components/Wizards/shared/components/MetaFilters/ObjectSelector';
import { useCallback, useEffect } from 'react';
import { useMenuScroller } from '../useMenuScroller';
import MultiSelect from '__components/Inputs/MultiSelect';
import useObjectOptions from '__components/Wizards/Dashlet/hooks/useObjectOptions';
import { useSelector } from 'react-redux';
import { getBusinessClientObject } from 'store/authentication/selectors';
import { useTranslation } from 'react-i18next';
import Icon from '__components/Kizen/Icon';
import { useTooltip } from '__components/Kizen/Tooltip';
import { LabelInfoIconSizing } from '../styles';
import { useSetupAssistant } from '@kizenapps/engine/react';
import { AssistantField, SelectOption } from '@kizenapps/engine';

export const CustomObjectBlock = ({
  field,
  disabled,
}: {
  field: AssistantField;
  disabled: boolean;
}) => {
  const {
    state,
    setState,
    getNestedFields,
    reInferFieldsForObject,
    evaluateExpression,
    shouldHideField,
    shouldDisableField,
    getFieldErrorState,
    registerFieldResetter,
    afterFieldChange,
    disabledKeys,
  } = useSetupAssistant();

  const value = (state as any)[field.key]?.value ??
    field.default ?? { label: '', value: '' };
  const changedObjectKey = field.key;

  const isDisabled = disabledKeys.includes(field.key) || disabled;

  const onChange = useCallback(
    (newValue: SelectOption | Array<SelectOption>) => {
      setState((prevState: any) => {
        const result = Array.isArray(newValue)
          ? newValue.map((nv) => ({
              id: nv.value,
              objectName: nv.label,
            }))
          : {
              id: newValue.value,
              objectName: newValue.label,
            };

        return {
          ...prevState,
          [field.key]: {
            type: 'custom_object',
            value: result,
          },
        };
      });

      // Field selectors that depend on the dynamic object ID need to be reset if the object ID changes
      const fieldDependencies = getNestedFields().filter((f: any) =>
        f.object_id.includes(changedObjectKey)
      );

      fieldDependencies.forEach((f: any) => {
        setState((prevState: any) => {
          return {
            ...prevState,
            [f.key]: undefined,
          };
        });
      });

      reInferFieldsForObject(changedObjectKey);

      afterFieldChange(field.key);
    },
    [
      field.key,
      setState,
      getNestedFields,
      reInferFieldsForObject,
      changedObjectKey,
      afterFieldChange,
    ]
  );

  useEffect(() => {
    registerFieldResetter(field.key, () => {
      setState((prevState: any) => {
        const newState = { ...prevState };
        delete newState[field.key];
        return newState;
      });
    });
  }, [field.key, registerFieldResetter, setState]);

  useEffect(() => {
    if (field.when) {
      evaluateExpression(field.when, field.key);
    }
  }, [field.when, evaluateExpression, field.key]);

  const menuProps = useMenuScroller();

  const [tooltipProps, tooltip] = useTooltip({ label: field.tooltip });

  const clientObject = useSelector(getBusinessClientObject);

  const clientObjectId = clientObject?.id;

  const { isLoading, ...objectsSelectProps } = useObjectOptions({
    useClient: true,
    enabled: Boolean(clientObjectId) && field.allow_multiple,
    clientObjectId,
  });

  const { t } = useTranslation();

  if (shouldHideField(field.key)) {
    return null;
  }

  const errorState = getFieldErrorState(field.key);

  if (field.allow_multiple) {
    return (
      <MultiSelect
        label={field.label}
        labelInfo={field.tooltip}
        placeholder={field.placeholder ?? t('Choose Object(s)')}
        className={`modal-multiselect objects ${
          isLoading ? 'loading' : 'ready'
        }`}
        value={
          Array.isArray(value)
            ? value.map((v) => {
                return { label: v.objectName, value: v.id };
              })
            : []
        }
        onChange={onChange}
        {...objectsSelectProps}
        isLoading={isLoading}
        {...menuProps}
        disabled={shouldDisableField(field.key) || isDisabled}
        required={field.required}
        validate={errorState}
      />
    );
  }

  return (
    <>
      <ObjectSelector
        label={
          <Typography>
            {field.label}
            {field.required ? '*' : ''}
            {field.tooltip && (
              <LabelInfoIconSizing size="10px">
                <Icon icon="info-circle" {...tooltipProps} />
              </LabelInfoIconSizing>
            )}
            {tooltip}
          </Typography>
        }
        onChange={onChange}
        disabled={shouldDisableField(field.key) || isDisabled}
        value={value}
        noSpacing
        validate={errorState}
        {...menuProps}
        required={field.required}
      />
    </>
  );
};
